import { useState, useEffect } from "react";
import { Phone, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { FIRM_PHONE, MAIN_OFFICE_PHONE, PEAK_PHONE } from "@/lib/content";

/**
 * CallNowFAB — Mobile-only floating call action.
 * Tap brass disc → expands into a menu of the three lines.
 * Hidden at lg breakpoint where header phones are visible.
 */
const HIDE_ON = ["/intake", "/estate-planning-frisco-plano"];

export default function CallNowFAB() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // These pages already show both call buttons inline.
  if (HIDE_ON.includes(pathname)) return null;

  return (
    <div className="lg:hidden fixed bottom-20 right-5 z-[60]" data-testid="call-now-fab">
      {/* Expanded menu */}
      {open && (
        <div className="absolute bottom-16 right-0 w-[280px] bg-ivory shadow-[0_18px_50px_-12px_rgba(13,27,42,0.45)] border border-transparent" data-testid="call-now-menu">
          <div className="px-5 pt-5 pb-3 border-b-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium">Call the Firm</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate2/60">Choose the right line</div>
          </div>

          <a
            href={MAIN_OFFICE_PHONE.href}
            className="block px-5 py-4 hover:bg-cream transition border-b-0"
            data-testid="fab-call-main-office"
          >
            <div className="text-[9px] uppercase tracking-[0.28em] text-slate2/60">Principal Office · Frisco</div>
            <div className="mt-1 font-display text-xl text-navy">{MAIN_OFFICE_PHONE.display}</div>
          </a>

          <a
            href={FIRM_PHONE.href}
            className="block px-5 py-4 hover:bg-cream transition border-b-0"
            data-testid="fab-call-toll-free"
          >
            <div className="text-[9px] uppercase tracking-[0.28em] text-slate2/60">Toll-Free · Statewide</div>
            <div className="mt-1 font-display text-xl text-navy">{FIRM_PHONE.display}</div>
          </a>

          <a
            href={PEAK_PHONE.href}
            className="block px-5 py-4 hover:bg-cream transition bg-brass/[0.06]"
            data-testid="fab-call-peak"
          >
            <div className="text-[9px] uppercase tracking-[0.28em] text-oxblood">PEAK American Financial</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-slate2/60">Clients &amp; Representatives Only</div>
            <div className="mt-1 font-cormorant italic text-xl text-oxblood">{PEAK_PHONE.display}</div>
          </a>

          <div className="px-5 py-3 text-[9px] uppercase tracking-[0.22em] text-slate2/50 text-center bg-cream/40">
            Attorney. CPA. Business Advisor.
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close call menu" : "Call the firm"}
        aria-expanded={open}
        className={`relative flex items-center justify-center h-14 w-14 rounded-full shadow-[0_10px_30px_-8px_rgba(198,168,124,0.55)] transition-all duration-300 ${
          open
            ? "bg-navy text-ivory rotate-90"
            : "bg-brass text-navy hover:bg-oxblood hover:text-ivory"
        }`}
        data-testid="call-now-fab-button"
      >
        {open ? <X size={22} strokeWidth={1.8} /> : <Phone size={22} strokeWidth={1.8} fill="currentColor" />}
        {!open && (
          <span className="absolute inset-0 rounded-full animate-ping bg-brass/40 pointer-events-none" />
        )}
      </button>
    </div>
  );
}
